import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Mail, Phone, Download, Upload, LayoutGrid, List } from "lucide-react";
import api from "../api";
import { useAuth, can } from "../auth";
import { Button, Input, Select, Field, Modal, Badge, EmptyState, Spinner, Textarea, Toast, ImageWithFallback } from "../components/common";
import { useViewPrefs } from "../hooks/useViewPrefs";
import { groupAndSort, distinctValues } from "../utils/grouping";

const STATUSES = ["lead", "prospect", "customer", "inactive"];
const REFERRER_TAG = "referrer";
const empty = { first_name: "", last_name: "", email: "", phone: "", title: "", status: "lead", company_id: "", linkedin_url: "", notes: "" };

const SORT_OPTIONS = ["newest", "oldest", "name_asc", "name_desc", "company_asc", "status_pipeline"];
const GROUP_OPTIONS = ["none", "status", "company"];
const VIEW_PREFS_KEY = "contactsViewPrefs";

const fullName = (c) => `${c.first_name || ""} ${c.last_name || ""}`.trim();
const SORT_FNS = {
  name_asc: (a, b) => fullName(a).localeCompare(fullName(b)),
  name_desc: (a, b) => fullName(b).localeCompare(fullName(a)),
  oldest: (a, b) => new Date(a.created_at) - new Date(b.created_at),
  company_asc: (a, b) => (a.company_name || "").localeCompare(b.company_name || ""),
  status_pipeline: (a, b) => STATUSES.indexOf(a.status) - STATUSES.indexOf(b.status),
  newest: (a, b) => new Date(b.created_at) - new Date(a.created_at),
};
const GROUP_KEY_OF = {
  status: (c) => c.status,
  company: (c) => c.company_name,
};

function ContactAvatar({ c, size = 40 }) {
  return (
    <ImageWithFallback src={c.gravatar_url} size={size}
      fallback={<span className="font-bold" style={{ fontSize: size >= 48 ? "1.1rem" : "0.875rem" }}>{c.first_name?.[0]?.toUpperCase()}</span>}
      fallbackClassName="bg-primary/15 text-primary" />
  );
}

export default function Contacts() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const writable = can.write(user);
  const [items, setItems] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [referrersOnly, setReferrersOnly] = useState(false);
  const [filterCompany, setFilterCompany] = useState("");
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(empty);
  const [toast, setToast] = useState(null);

  const [prefs, setPrefs] = useViewPrefs(VIEW_PREFS_KEY,
    { view: ["grid", "list"], sort: SORT_OPTIONS, groupBy: GROUP_OPTIONS },
    { view: "list", sort: "newest", groupBy: "none" });
  const { view, sort, groupBy } = prefs;

  const load = useCallback(() => {
    api.get("/contacts", { params: { search, status } }).then((r) => setItems(r.data));
  }, [search, status]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/companies").then((r) => setCompanies(r.data)); }, []);

  const openNew = () => { setForm(empty); setModal(true); };

  const save = async () => {
    const payload = { ...form, company_id: form.company_id || null };
    await api.post("/contacts", payload);
    setModal(false); load();
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const companyNames = useMemo(() => distinctValues(items || [], (c) => c.company_name), [items]);

  const visibleItems = useMemo(() => (items || [])
    .filter((c) => !referrersOnly || (c.tags || []).includes(REFERRER_TAG))
    .filter((c) => !filterCompany || c.company_name === filterCompany),
  [items, referrersOnly, filterCompany]);

  const groups = useMemo(() => {
    const raw = groupAndSort(visibleItems, {
      groupBy, groupKeyOf: GROUP_KEY_OF[groupBy], sortFn: SORT_FNS[sort] || SORT_FNS.newest,
      uncategorizedLabel: t("contact.uncategorized"),
    });
    if (groupBy !== "status") return raw;
    return raw.map((g) => (g.key === "__uncategorized" || g.key === "__all" ? g : { ...g, label: t(`statuses.${g.key}`) }));
  }, [visibleItems, groupBy, sort, t]);

  const exportCsv = () => {
    // Relative, same-origin -- matches api.js's baseURL (this can't go through
    // the axios instance: it's a browser-driven file download, not an XHR).
    window.open("/api/export/contacts.csv", "_blank");
  };
  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const r = await api.post("/import/contacts", fd, { headers: { "Content-Type": "multipart/form-data" } });
    const errs = r.data.errors?.length || 0;
    setToast({
      message: `${r.data.created} ${t("io.importDone")}${errs ? ` · ${errs} ${t("io.errors")}` : ""}`,
      type: errs ? "info" : "success",
    });
    setTimeout(() => setToast(null), 4000);
    e.target.value = "";
    load();
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{t("contact.title")}</h1>
        <div className="flex items-center gap-2">
          <div className="flex border border-border rounded-sm overflow-hidden">
            <button onClick={() => setPrefs((p) => ({ ...p, view: "grid" }))} data-testid="contacts-view-grid"
              className={`p-2 transition-colors ${view === "grid" ? "bg-primary text-white" : "text-muted hover:bg-surface"}`}><LayoutGrid size={16} /></button>
            <button onClick={() => setPrefs((p) => ({ ...p, view: "list" }))} data-testid="contacts-view-list"
              className={`p-2 transition-colors ${view === "list" ? "bg-primary text-white" : "text-muted hover:bg-surface"}`}><List size={16} /></button>
          </div>
          <button onClick={exportCsv} data-testid="export-contacts-btn" title={t("io.export")} className="p-2.5 rounded-sm border border-border text-muted hover:bg-surface transition-colors"><Download size={16} /></button>
          {writable && <>
            <input ref={fileRef} type="file" accept=".csv" onChange={importCsv} className="hidden" data-testid="import-contacts-input" />
            <button onClick={() => fileRef.current?.click()} data-testid="import-contacts-btn" title={t("io.import")} className="p-2.5 rounded-sm border border-border text-muted hover:bg-surface transition-colors"><Upload size={16} /></button>
            <Button onClick={openNew} data-testid="add-contact-btn"><Plus size={16} /><span className="hidden sm:inline">{t("contact.newContact")}</span></Button>
          </>}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
          <Input data-testid="contact-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("common.search")} className="pl-9" />
        </div>
        <Select data-testid="contact-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="sm:w-48">
          <option value="">{t("common.all")}</option>
          {STATUSES.map((s) => <option key={s} value={s}>{t(`statuses.${s}`)}</option>)}
        </Select>
        <Button variant={referrersOnly ? "primary" : "subtle"} className="py-1.5 px-3 text-xs whitespace-nowrap"
          onClick={() => setReferrersOnly(!referrersOnly)} data-testid="referrers-only-filter">
          {t("contact.referrersOnly")}
        </Button>
      </div>

      {items && items.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center">
          <div className="w-40 shrink-0">
            <Select aria-label={t("contact.sortBy")} data-testid="contact-sort" value={sort}
              onChange={(e) => setPrefs((p) => ({ ...p, sort: e.target.value }))}>
              {SORT_OPTIONS.map((s) => <option key={s} value={s}>{t(`contact.sort_${s}`)}</option>)}
            </Select>
          </div>
          <div className="w-40 shrink-0">
            <Select aria-label={t("contact.groupBy")} data-testid="contact-group-by" value={groupBy}
              onChange={(e) => setPrefs((p) => ({ ...p, groupBy: e.target.value }))}>
              <option value="none">{t("contact.groupNone")}</option>
              <option value="status">{t("contact.groupByStatus")}</option>
              <option value="company">{t("contact.groupByCompany")}</option>
            </Select>
          </div>
          {companyNames.length > 0 && (
            <div className="w-40 shrink-0">
              <Select aria-label={t("contact.company")} data-testid="contact-filter-company" value={filterCompany}
                onChange={(e) => setFilterCompany(e.target.value)}>
                <option value="">{t("contact.allCompanies")}</option>
                {companyNames.map((n) => <option key={n} value={n}>{n}</option>)}
              </Select>
            </div>
          )}
        </div>
      )}

      {!items ? <Spinner /> : visibleItems.length === 0 ? (
        <EmptyState title={t("common.noResults")} action={writable && <Button onClick={openNew}><Plus size={16} />{t("contact.newContact")}</Button>} />
      ) : (
        <div className="space-y-6">
          {groups.map((group, gi) => (
            <div key={group.key}>
              {group.label !== null && (
                <div className="flex items-center gap-2 mb-2 px-1">
                  <h3 className="text-xs uppercase tracking-[0.15em] text-muted font-semibold">{group.label}</h3>
                  <span className="text-xs text-muted">({group.items.length})</span>
                </div>
              )}
              {view === "grid" ? (
                <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 ${gi === 0 ? "stagger" : ""}`}>
                  {group.items.map((c) => (
                    <div key={c.id} data-testid={`contact-card-${c.id}`} onClick={() => navigate(`/contacts/${c.id}`)} className="border border-border rounded-sm p-5 hover:-translate-y-px transition-transform duration-200 cursor-pointer">
                      <ContactAvatar c={c} />
                      <div className="font-display font-bold text-lg mt-3 truncate">{c.first_name} {c.last_name}</div>
                      <div className="text-sm text-muted truncate">{c.title || "—"}{c.company_name && ` · ${c.company_name}`}</div>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <Badge value={c.status} label={t(`statuses.${c.status}`)} />
                        {(c.tags || []).includes(REFERRER_TAG) && <Badge value="won" label={t("contact.regularReferrer")} />}
                      </div>
                      {(c.email || c.phone) && (
                        <div className="text-xs text-muted mt-2 space-y-1">
                          {c.email && <div className="flex items-center gap-1 truncate"><Mail size={11} className="shrink-0" />{c.email}</div>}
                          {c.phone && <div className="flex items-center gap-1"><Phone size={11} className="shrink-0" />{c.phone}</div>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="border border-border rounded-sm overflow-hidden">
                  {group.items.map((c) => (
                    <div key={c.id} data-testid={`contact-row-${c.id}`} onClick={() => navigate(`/contacts/${c.id}`)} className="flex items-center gap-3 px-4 py-3 border-b border-border last:border-0 hover:bg-surface transition-colors cursor-pointer">
                      <ContactAvatar c={c} size={36} />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">
                          {c.first_name} {c.last_name} {c.title && <span className="text-muted font-normal">· {c.title}</span>}
                        </div>
                        <div className="text-xs text-muted truncate flex items-center gap-3">
                          {c.company_name && <span>{c.company_name}</span>}
                          {c.email && <span className="hidden sm:flex items-center gap-1"><Mail size={11} />{c.email}</span>}
                          {c.phone && <span className="hidden sm:flex items-center gap-1"><Phone size={11} />{c.phone}</span>}
                        </div>
                      </div>
                      <Badge value={c.status} label={t(`statuses.${c.status}`)} />
                      {(c.tags || []).includes(REFERRER_TAG) && <Badge value="won" label={t("contact.regularReferrer")} />}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Modal open={modal} onClose={() => setModal(false)} title={t("contact.newContact")}
        footer={<>
          <Button variant="ghost" onClick={() => setModal(false)}>{t("common.cancel")}</Button>
          <Button onClick={save} data-testid="save-contact-btn">{t("common.save")}</Button>
        </>}>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("contact.first_name")}><Input data-testid="contact-first-name" value={form.first_name} onChange={set("first_name")} /></Field>
          <Field label={t("contact.last_name")}><Input value={form.last_name || ""} onChange={set("last_name")} /></Field>
        </div>
        <Field label={t("contact.email")}><Input type="email" value={form.email || ""} onChange={set("email")} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("contact.phone")}><Input value={form.phone || ""} onChange={set("phone")} /></Field>
          <Field label={t("contact.jobtitle")}><Input value={form.title || ""} onChange={set("title")} /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("contact.status")}>
            <Select value={form.status} onChange={set("status")}>
              {STATUSES.map((s) => <option key={s} value={s}>{t(`statuses.${s}`)}</option>)}
            </Select>
          </Field>
          <Field label={t("contact.company")}>
            <Select value={form.company_id || ""} onChange={set("company_id")}>
              <option value="">—</option>
              {companies.map((co) => <option key={co.id} value={co.id}>{co.name}</option>)}
            </Select>
          </Field>
        </div>
        <Field label={t("contact.linkedinUrl")}><Input value={form.linkedin_url || ""} onChange={set("linkedin_url")} placeholder="https://linkedin.com/in/…" /></Field>
        <Field label={t("contact.notes")}><Textarea rows={3} value={form.notes || ""} onChange={set("notes")} /></Field>
      </Modal>

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}
